import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import AuthModal from '../components/AuthModal';
import { generateCSRFToken } from '../utils/security';

// ─── Session helpers ──────────────────────────────────────────────────────────
// Single source of truth for reading/writing the auth session.
// All other modules (InsightLibraryPage, PremiumModal) must use these.

export const SESSION_KEY   = 'auth_session';
export const DEV_USER_KEY  = 'dev_auth_user';
const CURRENT_SCHEMA_VERSION = 3; // bump when TOKEN_SECRET rotates

export const readSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.verified || !s?.email || !s?.expiresAt) return null;
    if (s.expiresAt < Date.now()) return null;
    // Schema version check: clear sessions from before last secret rotation
    if ((s.schemaVersion || 0) < CURRENT_SCHEMA_VERSION) return null;
    return s;
  } catch { return null; }
};

export const readVerifiedEmail = () => readSession()?.email || null;

export const readSessionToken = () => readSession()?.token || null;

export const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(DEV_USER_KEY);
  sessionStorage.clear();
  // Send LOGOUT (not CLEAR_CACHE) to preserve static PWA shell cache.
  // LOGOUT clears only dynamic cache — prevents blank screen after logout.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(regs => {
      regs.forEach(reg => reg.active?.postMessage({ type: 'LOGOUT' }));
    }).catch(() => {});
  }
};

// ─── Role resolution ─────────────────────────────────────────────────────────
// Role comes ONLY from the session object, which is written by AuthVerify
// using the server-returned role (sourced from DB admin_roles table).
// Client-side email comparison is intentionally removed — it was a
// privilege-escalation risk (admin email visible in compiled bundle).
const determineRole = (email) => {
  if (!email) return 'user';
  // Read role from the stored session — set by server at login time
  try {
    const raw = localStorage.getItem('auth_session');
    if (raw) {
      const s = JSON.parse(raw);
      if (s?.email?.toLowerCase() === email.toLowerCase() && s?.role) {
        return s.role;
      }
    }
  } catch { /* fall through */ }
  return 'user';
};

// ─── Build user object from session ──────────────────────────────────────────
const buildUserFromSession = (session) => {
  if (!session?.email) return null;
  return {
    id:                  session.deviceId || `session_${session.email}`,
    email:               session.email,
    email_confirmed_at:  session.loginTime
      ? new Date(session.loginTime).toISOString()
      : new Date().toISOString(),
    user_metadata: {
      name:     session.email.split('@')[0],
      role:     session.role || 'user',
      deviceId: session.deviceId,
    },
    role: session.role || 'user',
  };
};

// ─── Context ──────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Fallback when used outside provider (e.g. during SSR or test)
    const session = readSession();
    return {
      user:              session ? buildUserFromSession(session) : null,
      isVerified:        !!session,
      verifiedEmail:     session?.email || null,
      loading:           false,
      userRole:          session?.role || 'user',
      isAdmin:           determineRole(session?.email) === 'admin',
      signOut:           clearSession,
      sessionToken:      session?.token || null,
      currentUserProfile: null,
      setCurrentUserProfile: () => {}
    };
  }
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user,      setUser]      = useState(() => buildUserFromSession(readSession()));
  const [loading,   setLoading]   = useState(true);
  const [userRole,  setUserRole]  = useState(() => determineRole(readSession()?.email));
  const [sessionToken, setSessionToken] = useState(() => readSessionToken());
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  const mountedRef          = useRef(true);
  const authShownRef        = useRef(false);
  const sessionIntervalRef  = useRef(null);
  const eventDebounceRef    = useRef(null);

  const fetchUserProfile = useCallback(async (email) => {
    if (!email || !supabase) {
      setCurrentUserProfile(null);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', email)
        .maybeSingle();
      if (!error && data) {
        setCurrentUserProfile(data);
      }
    } catch (_) {}
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────
  // Wrapped in useMemo so they recompute whenever `user` or `userRole` changes.
  const { isVerified, verifiedEmail } = useMemo(() => {
    const session = readSession();
    const verified = !!(user && session?.verified && session?.email === user.email);
    return {
      isVerified:    verified,
      verifiedEmail: verified ? session.email : null,
    };
  }, [user]);
  const isAdmin = userRole === 'admin';

  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // ── Initialize on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    const init = async () => {
      const s = readSession();
      if (s) {
        const u = buildUserFromSession(s);
        const role = determineRole(s.email);
        if (mountedRef.current) {
          setUser(u);
          setUserRole(role);
          setLoading(false);
        }
        fetchUserProfile(s.email);
        return;
      }

      // Fallback: check Supabase auth (for users who signed in via Supabase directly)
      if (supabase) {
        try {
          const { data: { session: sbSession } } = await supabase.auth.getSession();
          if (sbSession && mountedRef.current) {
            setUser(sbSession.user);
            setUserRole(determineRole(sbSession.user.email));
            setSessionToken(sbSession.access_token);
            fetchUserProfile(sbSession.user.email);
          }
        } catch (_) {}
      }

      if (mountedRef.current) setLoading(false);
    };

    init();

    // ── Session expiry monitor — check every 60s ──────────────────────────────
    sessionIntervalRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      const s = readSession();
      if (!s && userRef.current) {
        // Session expired — clear state and broadcast to all tabs
        setUser(null);
        setUserRole('user');
        setSessionToken(null);
        setCurrentUserProfile(null);
        try {
          const bc = new BroadcastChannel('tv-auth-sync');
          bc.postMessage({ type: 'SESSION_REVOKED' });
          bc.close();
        } catch (_) {}
      }
    }, 60000);

    // ── Auth state change events ──────────────────────────────────────────────
    // Fired by AuthVerify after successful magic-link verification
    const handleAuthStateChange = (event) => {
      if (!mountedRef.current || !event.detail?.user) return;
      const userData = event.detail.user;

      // Debounce rapid-fire events (e.g. multiple storage writes)
      if (eventDebounceRef.current) clearTimeout(eventDebounceRef.current);
      eventDebounceRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        const role = determineRole(userData.email);
        setUser(userData);
        setUserRole(role);
        setSessionToken(readSessionToken());
        setLoading(false);
        fetchUserProfile(userData.email);

        if (!authShownRef.current) {
          authShownRef.current = true;
          toast.success(`Welcome back, ${userData.email.split('@')[0]}! 🎉`, {
            duration: 4000,
            position: 'top-right',
          });
        }
      }, 200);
    };

    // Re-validate session when tab regains focus
    const handleFocus = () => {
      if (!mountedRef.current) return;
      const s = readSession();
      if (!s && userRef.current) {
        setUser(null);
        setUserRole('user');
        setSessionToken(null);
        setCurrentUserProfile(null);
      }
    };

    // Multi-tab BroadcastChannel & Storage Event Listeners
    let bc;
    try {
      bc = new BroadcastChannel('tv-auth-sync');
      bc.onmessage = (event) => {
        if (!mountedRef.current) return;
        const { type } = event.data || {};
        if (type === 'LOGOUT' || type === 'SESSION_REVOKED') {
          setUser(null);
          setUserRole('user');
          setSessionToken(null);
          setCurrentUserProfile(null);
        } else if (type === 'LOGIN') {
          const s = readSession();
          if (s) {
            setUser(buildUserFromSession(s));
            setUserRole(determineRole(s.email));
            setSessionToken(s.token);
            fetchUserProfile(s.email);
          }
        }
      };
    } catch (_) {}

    const handleStorageChange = (e) => {
      if (!mountedRef.current) return;
      if (e.key === SESSION_KEY) {
        if (!e.newValue) {
          setUser(null);
          setUserRole('user');
          setSessionToken(null);
          setCurrentUserProfile(null);
        } else {
          const s = readSession();
          if (s) {
            setUser(buildUserFromSession(s));
            setUserRole(determineRole(s.email));
            setSessionToken(s.token);
            fetchUserProfile(s.email);
          }
        }
      }
    };

    window.addEventListener('auth-state-change',  handleAuthStateChange);
    window.addEventListener('focus',               handleFocus);
    window.addEventListener('storage',             handleStorageChange);

    return () => {
      mountedRef.current = false;
      clearInterval(sessionIntervalRef.current);
      clearTimeout(eventDebounceRef.current);
      if (bc) {
        try { bc.close(); } catch (_) {}
      }
      window.removeEventListener('auth-state-change',  handleAuthStateChange);
      window.removeEventListener('focus',               handleFocus);
      window.removeEventListener('storage',             handleStorageChange);
    };
  }, [fetchUserProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track Session Duration (Time Consume) ──
  useEffect(() => {
    if (!isVerified || !verifiedEmail || !sessionToken) return;

    let totalSeconds = 0;
    const interval = setInterval(() => {
      totalSeconds += 10;
      
      // Update server every 60 seconds
      if (totalSeconds % 60 === 0 && navigator.onLine) {
        try {
          const csrfToken = generateCSRFToken();
          fetch('/api/auth?action=ping', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-csrf-token': csrfToken,
              'Origin': window.location.origin
            },
            body: JSON.stringify({
              token: sessionToken,
              email: verifiedEmail,
              duration: totalSeconds
            })
          }).catch(() => {});
        } catch (_) {}
      }
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [isVerified, verifiedEmail, sessionToken]);

  // ── Sign out ────────────────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    // 1. Revoke server session (fire-and-forget, never blocks logout)
    const tokenToRevoke = readSessionToken();
    const emailToRevoke = readVerifiedEmail();
    if (tokenToRevoke && emailToRevoke && navigator.onLine) {
      try {
        const csrfToken = generateCSRFToken();
        fetch('/api/auth?action=revoke', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken, 'Origin': window.location.origin },
          body:    JSON.stringify({ token: tokenToRevoke, email: emailToRevoke }),
        }).catch(() => {});
      } catch (_) {}
    }

    // 2. Clear local state immediately
    clearSession();
    setUser(null);
    setUserRole('user');
    setCurrentUserProfile(null);
    authShownRef.current = false;

    // 3. Broadcast logout to all open tabs
    try {
      const bc = new BroadcastChannel('tv-auth-sync');
      bc.postMessage({ type: 'LOGOUT' });
      bc.close();
    } catch (_) {}

    // 4. Tell SW to clear dynamic cache only (static cache preserved)
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'LOGOUT' });
    }

    if (supabase) {
      try { await supabase.auth.signOut(); } catch (_) {}
    }

    toast.success('Signed out successfully');
  }, []);

  const [authModal, setAuthModal] = useState({ isOpen: false, action: 'comment', onSuccess: null });

  const showLogin = useCallback((action = 'comment', onSuccess = null) => {
    setAuthModal({ isOpen: true, action, onSuccess });
  }, []);

  const closeLogin = useCallback(() => {
    setAuthModal(prev => ({ ...prev, isOpen: false, onSuccess: null }));
  }, []);

  const handleAuthSuccess = useCallback(() => {
    const fn = authModal.onSuccess;
    closeLogin();
    if (typeof fn === 'function') {
      try { fn(); } catch (err) { console.error('[AuthContext] Resume callback error:', err); }
    }
  }, [authModal, closeLogin]);

  const isProperlyAuthenticated = useCallback(() => isVerified, [isVerified]);
  const getSessionInfo          = useCallback(() => readSession(), []);

  const value = useMemo(() => ({
    user,
    isVerified,
    verifiedEmail,   // ← always the server-verified email, never user-supplied
    sessionToken,    // ← HMAC token from the verified session
    loading,
    userRole,
    isAdmin,
    signOut,
    // Global Auth Modal
    authModal,
    showLogin,
    closeLogin,
    handleAuthSuccess,
    // Convenience helpers
    isProperlyAuthenticated,
    getSessionInfo,
    // Cached user profile
    currentUserProfile,
    setCurrentUserProfile
  }), [
    user,
    isVerified,
    verifiedEmail,
    sessionToken,
    loading,
    userRole,
    isAdmin,
    signOut,
    authModal,
    showLogin,
    closeLogin,
    handleAuthSuccess,
    isProperlyAuthenticated,
    getSessionInfo,
    currentUserProfile
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AuthModal 
        isOpen={authModal.isOpen} 
        onClose={closeLogin} 
        onSuccess={handleAuthSuccess} 
        action={authModal.action} 
      />
    </AuthContext.Provider>
  );
};

export default AuthContext;
